import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import PageHeader from '../components/PageHeader';
import TurnstileWidget from '../components/TurnstileWidget';

const ContactContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Description = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
`;

const ContactForm = styled(Form)`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  border: 2px solid rgba(255, 51, 51, 0.3);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled(Field)`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 51, 51, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const TextArea = styled(Field)`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 51, 51, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Button = styled.button`
  background: rgba(255, 51, 51, 0.8);
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 51, 51, 1);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff3333;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const ContactInfo = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 2px solid rgba(255, 51, 51, 0.3);
  
  p {
    margin: 1rem 0;
    color: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.1rem;
    
    svg {
      color: #ff3333;
    }
  }
`;

const SuccessMessage = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: fadeIn 0.3s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuccessIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 51, 51, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #ff3333;
  border: 2px solid rgba(255, 51, 51, 0.3);
`;

const SuccessText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  flex: 1;
`;

const TurnstileContainer = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  
  > div {
    transform: scale(0.9);
    -webkit-transform: scale(0.9);
    transform-origin: 50% 50%;
    -webkit-transform-origin: 50% 50%;
  }

  @media (max-width: 768px) {
    > div {
      transform: scale(0.85);
      -webkit-transform: scale(0.85);
    }
  }
`;

const disposableEmailDomains = [
  'tempmail.com', 'throwawaymail.com', 'mailinator.com', 
  'temp-mail.org', 'guerrillamail.com', '10minutemail.com'
];

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Full name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'Name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format')
    .max(254, 'Email must be less than 254 characters')
    .test('no-disposable', 'Please use a valid business email', value => {
      if (!value) return true;
      const domain = value.split('@')[1];
      return !disposableEmailDomains.includes(domain?.toLowerCase());
    })
    .test('email-typo', 'Did you mean to type .com?', value => {
      if (!value) return true;
      if (value.endsWith('.con')) return false;
      return true;
    }),
  subject: Yup.string()
    .required('Subject is required')
    .min(2, 'Subject must be at least 2 characters')
    .max(100, 'Subject must be less than 100 characters'),
  message: Yup.string()
    .required('Message is required')
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message must be less than 1000 characters')
});

const FormattedNameInput = ({ field, form, ...props }) => {
  const handleChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s'-]*$/.test(value)) {
      const formatted = value.replace(/\b\w/g, char => char.toUpperCase());
      form.setFieldValue(field.name, formatted);
    }
  };

  return (
    <Input
      {...field}
      {...props}
      onChange={handleChange}
      type="text"
      maxLength={50}
    />
  );
};

const Contact = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState('');
  const turnstileRef = React.useRef(null);

  // Load Turnstile script and set up the callback
  useEffect(() => {
    setTurnstileKey(process.env.REACT_APP_TURNSTILE_SITE_KEY || '');
  }, []);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Get the token from Turnstile
      const token = turnstileToken;
      if (!token) {
        setSubmitError('Please complete the Cloudflare Turnstile verification');
        setSubmitting(false);
        return;
      }

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          subject: values.subject,
          message: values.message,
          turnstileToken: token
        })
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setSubmitError(data?.error || 'Failed to send message. Please try again.');
        setTurnstileToken(null);
        turnstileRef.current?.reset?.();
        setSubmitting(false);
        return;
      }

      setShowSuccess(true);
      setSubmitError('');
      resetForm();
      setTurnstileToken(null);
      turnstileRef.current?.reset?.();
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('Failed to send message. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <ContactContainer>
      <PageHeader title="Get In Touch" highlightWord="In" />
      <Description>
        <p>
          Jot us a message and we'll get back to you as quickly as possible.
        </p>
      </Description>
      <Formik
        initialValues={{ name: '', email: '', subject: '', message: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <ContactForm>
            {showSuccess && (
              <SuccessMessage>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessText>
                  Message sent successfully! We'll get back to you soon.
                </SuccessText>
              </SuccessMessage>
            )}
            
            {submitError && (
              <ErrorMessage style={{ marginBottom: '1rem', textAlign: 'center' }}>
                {submitError}
              </ErrorMessage>
            )}

            <FormGroup>
              <Label htmlFor="name">Your Name</Label>
              <Field
                name="name"
                component={FormattedNameInput}
                id="name"
                placeholder="John Doe"
              />
              {errors.name && touched.name && (
                <ErrorMessage>{errors.name}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Your Email</Label>
              <Input 
                type="email" 
                id="email" 
                name="email" 
                placeholder="john@company.com"
              />
              {errors.email && touched.email && (
                <ErrorMessage>{errors.email}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input 
                type="text" 
                id="subject" 
                name="subject" 
                placeholder="How can we help you?"
              />
              {errors.subject && touched.subject && (
                <ErrorMessage>{errors.subject}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Your Message</Label>
              <TextArea
                component="textarea"
                id="message"
                name="message"
                rows="6"
                placeholder="Tell us more about your needs..."
              />
              {errors.message && touched.message && (
                <ErrorMessage>{errors.message}</ErrorMessage>
              )}
            </FormGroup>

            <TurnstileContainer>
              <TurnstileWidget
                ref={turnstileRef}
                siteKey={turnstileKey}
                theme="dark"
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
              />
            </TurnstileContainer>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </ContactForm>
        )}
      </Formik>

      <ContactInfo>
        <p>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
          </svg>
          <a style={{ color: 'white', textDecoration: 'none' }} href="mailto:info@redcloudstorage.net">Info@redcloudstorage.net</a>
        </p>
        <p>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="currentColor"/>
          </svg>
          Monday - Friday, 9:00 AM - 5:00 PM PDT
        </p>
        <p>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
          </svg>
          San Diego, California
        </p>
      </ContactInfo>
    </ContactContainer>
  );
};

export default Contact;
